import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, ChevronDown, ChevronRight, Edit3, Globe2, History, MessageSquarePlus, Paperclip, Plus, Send, Trash2, X } from 'lucide-react';
import { useApiClient } from '../auth/useApiClient';

const QUERY_PREFS_KEY = 'execEye.queryPreferences';

const defaultConversationStarters = [
  {
    label: 'Shortlist programmes',
    message: 'Shortlist 3 best-fit LBS programmes for a senior manager moving into general leadership and explain how to position each one.'
  },
  {
    label: 'Compare options',
    message: 'Compare the strongest options by audience, outcomes, format, and likely objections.'
  },
  {
    label: 'Check formats',
    message: 'Which recommended options are available online, on-campus, or in London?'
  },
  {
    label: 'Find evidence',
    message: 'Show participant evidence or testimonials I can use in a client conversation.'
  }
];

async function readApiJson(response) {
  try {
    return await response.json();
  } catch (error) {
    return {};
  }
}

function getApiErrorMessage(response, data, fallback) {
  const apiMessage = [data?.message, data?.error].filter(Boolean).join(': ');
  if (apiMessage) return apiMessage;

  if (response?.status === 401) {
    return 'Please sign in again to use the knowledge assistant.';
  }
  if (response?.status === 403) {
    return 'Your account does not currently have access to the knowledge assistant.';
  }
  if (response?.status === 404) {
    return 'The knowledge assistant API was not found. Please check the deployed backend URL.';
  }

  return fallback;
}

function getModelUnavailableLabel(errorMessage = '') {
  if (/access|permission|sign in/i.test(errorMessage)) return 'Access unavailable';
  if (/not found|api url|backend/i.test(errorMessage)) return 'API unavailable';
  return 'Models unavailable';
}

function readQueryPreferences() {
  try {
    return JSON.parse(window.localStorage.getItem(QUERY_PREFS_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

const QueryInterface = () => {
  const { request } = useApiClient();
  const initialPreferencesRef = useRef(readQueryPreferences());
  const initialPreferences = initialPreferencesRef.current;
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [threads, setThreads] = useState([]);
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [editingThreadId, setEditingThreadId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [models, setModels] = useState([]);
  const [modelLoadError, setModelLoadError] = useState('');
  const [selectedModel, setSelectedModel] = useState(initialPreferences.selectedModel || '');
  const [reasoningEffort, setReasoningEffort] = useState(initialPreferences.reasoningEffort || '');
  const [webSearchEnabled, setWebSearchEnabled] = useState(Boolean(initialPreferences.webSearchEnabled));
  const [conversationStarters, setConversationStarters] = useState(defaultConversationStarters);
  const [modelMenuOpen, setModelMenuOpen] = useState(false);
  const [hoveredReasoningModel, setHoveredReasoningModel] = useState('');
  const [isThreadDrawerOpen, setIsThreadDrawerOpen] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatStreamRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const modelMenuRef = useRef(null);
  const currentThreadIdRef = useRef(null);
  const viewVersionRef = useRef(0);
  const activeQueryRequestRef = useRef(0);

  const loadThreads = useCallback(async () => {
    try {
      const response = await request('/api/chat/threads');
      if (!response.ok) return;
      const data = await response.json();
      setThreads(Array.isArray(data.threads) ? data.threads : []);
    } catch (error) {
      setThreads([]);
    }
  }, [request]);

  const loadModels = useCallback(async () => {
    try {
      setModelLoadError('');
      const response = await request('/api/openai/models');
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(getApiErrorMessage(response, data, 'Unable to load available models.'));
      }
      const availableModels = Array.isArray(data.models) ? data.models : [];
      if (availableModels.length === 0) {
        throw new Error('No models are currently available.');
      }
      setModels(availableModels);
      setSelectedModel((currentModel) => {
        const persistedModel = currentModel || initialPreferences.selectedModel || '';
        const matchedModel = availableModels.find((item) => item.id === persistedModel);
        const nextModel = matchedModel || availableModels[0];
        if (!nextModel) return '';

        setReasoningEffort((currentEffort) => {
          if (!nextModel.supportsReasoning) return '';
          if (currentEffort && nextModel.reasoningEfforts?.includes(currentEffort)) return currentEffort;
          if (initialPreferences.reasoningEffort && nextModel.reasoningEfforts?.includes(initialPreferences.reasoningEffort)) {
            return initialPreferences.reasoningEffort;
          }
          return nextModel.reasoningEfforts?.[1] || nextModel.reasoningEfforts?.[0] || '';
        });

        return nextModel.id;
      });
    } catch (error) {
      setModels([]);
      setSelectedModel('');
      setReasoningEffort('');
      setModelLoadError(error.message || 'Models are currently unavailable.');
    }
  }, [initialPreferences.reasoningEffort, initialPreferences.selectedModel, request]);

  useEffect(() => {
    let isMounted = true;

    const loadConversationStarters = async () => {
      try {
        const response = await request('/api/query/conversation-starters');
        if (!response.ok) return;
        const data = await response.json();
        const starters = Array.isArray(data.starters)
          ? data.starters
              .map((starter) => ({
                label: (starter.label || '').trim(),
                message: (starter.message || '').trim()
              }))
              .filter((starter) => starter.label && starter.message)
          : [];

        if (isMounted && starters.length > 0) {
          setConversationStarters(starters);
        }
      } catch (error) {
        // Keep default conversation starters when settings are unavailable.
      }
    };

    loadConversationStarters();

    return () => {
      isMounted = false;
    };
  }, [request]);

  useEffect(() => {
    loadThreads();
    loadModels();
  }, [loadModels, loadThreads]);

  useEffect(() => {
    window.localStorage.setItem(QUERY_PREFS_KEY, JSON.stringify({
      selectedModel,
      reasoningEffort,
      webSearchEnabled
    }));
  }, [reasoningEffort, selectedModel, webSearchEnabled]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!modelMenuRef.current || modelMenuRef.current.contains(event.target)) return;
      setModelMenuOpen(false);
      setHoveredReasoningModel('');
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const selected = models.find((item) => item.id === selectedModel);
    if (!selected?.supportsReasoning) {
      setReasoningEffort('');
    } else if (!reasoningEffort) {
      setReasoningEffort(selected.reasoningEfforts?.[1] || selected.reasoningEfforts?.[0] || '');
    }
  }, [models, reasoningEffort, selectedModel]);

  useEffect(() => {
    if (chatStreamRef.current) {
      chatStreamRef.current.scrollTop = chatStreamRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 24 * 4;
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [query]);

  const buildHistory = (msgs) =>
    msgs.map(m => ({
      role: m.type === 'user' ? 'user' : 'assistant',
      content: m.text
    }));

  const renderMessageBody = (text) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
      {text || ''}
    </ReactMarkdown>
  );

  const startNewChat = () => {
    viewVersionRef.current += 1;
    currentThreadIdRef.current = null;
    setCurrentThreadId(null);
    setMessages([]);
    setQuery('');
    setAttachedFiles([]);
    setIsThreadDrawerOpen(false);
    setLoading(false);
  };

  const loadThreadMessages = async (threadId) => {
    viewVersionRef.current += 1;
    const loadViewVersion = viewVersionRef.current;
    currentThreadIdRef.current = threadId;
    setCurrentThreadId(threadId);
    setIsThreadDrawerOpen(false);
    setLoading(true);
    try {
      const response = await request(`/api/chat/threads/${threadId}/messages`);
      if (!response.ok) {
        throw new Error('Unable to load chat.');
      }
      const data = await response.json();
      const nextMessages = (Array.isArray(data.messages) ? data.messages : []).map((item) => ({
        id: item.id,
        type: item.role === 'user' ? 'user' : 'ai',
        text: item.content,
        files: item.metadata?.files || []
      }));
      if (viewVersionRef.current === loadViewVersion) {
        setMessages(nextMessages);
        setAttachedFiles([]);
      }
    } catch (error) {
      if (viewVersionRef.current === loadViewVersion) {
        setMessages([{ type: 'ai', text: 'Unable to load this chat.' }]);
      }
    }
    if (viewVersionRef.current === loadViewVersion) {
      setLoading(false);
    }
  };

  const renameThread = async (threadId) => {
    const title = editingTitle.trim();
    if (!title) return;

    try {
      const response = await request(`/api/chat/threads/${threadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title })
      });
      if (response.ok) {
        await loadThreads();
      }
    } finally {
      setEditingThreadId(null);
      setEditingTitle('');
    }
  };

  const removeThread = async (threadId) => {
    if (!window.confirm('Delete this chat?')) return;

    const response = await request(`/api/chat/threads/${threadId}`, { method: 'DELETE' });
    if (response.ok) {
      if (currentThreadId === threadId) {
        startNewChat();
      }
      await loadThreads();
    }
  };

  const handleFileSelection = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    setAttachedFiles((prev) => [...prev, ...selectedFiles].slice(0, 5));
    event.target.value = '';
  };

  const removeAttachedFile = (indexToRemove) => {
    setAttachedFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleQuery = async (text) => {
    const q = (text || query).trim();
    if (!q && attachedFiles.length === 0) return;
    const requestViewVersion = viewVersionRef.current;
    const requestThreadId = currentThreadIdRef.current;
    const requestId = activeQueryRequestRef.current + 1;
    activeQueryRequestRef.current = requestId;
    const filesForMessage = attachedFiles.map((file) => ({
      filename: file.name,
      byteSize: file.size,
      mimeType: file.type
    }));
    const userMessageText = q || 'Please review the attached file(s).';
    const newMessages = [...messages, { type: 'user', text: userMessageText, files: filesForMessage }];
    setMessages(newMessages);
    setLoading(true);
    setQuery('');
    setAttachedFiles([]);
    try {
      const formData = new FormData();
      formData.append('query', q);
      formData.append('history', JSON.stringify(buildHistory(messages)));
      formData.append('webSearch', webSearchEnabled ? 'true' : 'false');
      if (requestThreadId) formData.append('threadId', requestThreadId);
      if (selectedModel) formData.append('model', selectedModel);
      if (reasoningEffort) formData.append('reasoningEffort', reasoningEffort);
      attachedFiles.forEach((file) => formData.append('files', file));

      const response = await request('/api/query', {
        method: 'POST',
        body: formData
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(getApiErrorMessage(response, data, 'Unable to reach the knowledge assistant.'));
      }
      const assistantText = data.response || 'No response received.';
      const isStillVisibleRequest = viewVersionRef.current === requestViewVersion && activeQueryRequestRef.current === requestId;
      if (isStillVisibleRequest && data.threadId) {
        currentThreadIdRef.current = data.threadId;
        setCurrentThreadId(data.threadId);
      }
      if (isStillVisibleRequest) {
        setMessages(prev => [...prev, { type: 'ai', text: assistantText }]);
      }
      await loadThreads();
    } catch (err) {
      if (viewVersionRef.current === requestViewVersion && activeQueryRequestRef.current === requestId) {
        const message = err?.message && err.message !== 'Failed to fetch'
          ? err.message
          : 'Unable to reach the knowledge assistant backend. Please check the deployed API URL and backend service.';
        setMessages(prev => [...prev, { type: 'ai', text: message }]);
      }
    }
    if (viewVersionRef.current === requestViewVersion && activeQueryRequestRef.current === requestId) {
      setLoading(false);
    }
  };

  const selectedModelDetails = models.find((item) => item.id === selectedModel);
  const modelButtonLabel = selectedModelDetails
    ? `${selectedModelDetails.label}${reasoningEffort ? ` / ${reasoningEffort}` : ''}`
    : modelLoadError ? getModelUnavailableLabel(modelLoadError) : 'Select model';

  const selectModel = (model, effort = '') => {
    setSelectedModel(model.id);
    if (model.supportsReasoning) {
      setReasoningEffort(effort || model.reasoningEfforts?.[1] || model.reasoningEfforts?.[0] || '');
    } else {
      setReasoningEffort('');
    }
    setModelMenuOpen(false);
    setHoveredReasoningModel('');
  };

  return (
    <section className="page page-query">
      <div className="query-toolbar">
        <button
          type="button"
          className="icon-btn query-toolbar-icon-btn"
          onClick={startNewChat}
          aria-label="New chat"
          title="New chat"
        >
          <MessageSquarePlus size={16} />
        </button>
        <button
          type="button"
          className="icon-btn query-toolbar-icon-btn"
          onClick={() => setIsThreadDrawerOpen(true)}
          aria-label="Saved chats"
          title="Saved chats"
        >
          <History size={16} />
        </button>
      </div>

      {isThreadDrawerOpen && (
        <div className="pathway-history-overlay query-history-overlay" role="presentation" onClick={() => setIsThreadDrawerOpen(false)}>
          <aside
            className="card pathway-history-drawer chat-history-drawer schema-scroll"
            aria-label="Saved chats"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="chat-history-header">
              <div className="section-label">Saved Chats</div>
              <div className="pathway-history-actions">
                <button type="button" className="icon-btn" onClick={startNewChat} aria-label="New chat">
                  <MessageSquarePlus size={16} />
                </button>
                <button type="button" className="icon-btn" onClick={() => setIsThreadDrawerOpen(false)} aria-label="Close saved chats">
                  <X size={14} />
                </button>
              </div>
            </div>
            <div className="chat-thread-list">
              {threads.length === 0 && <p className="assistant-info">No saved chats yet.</p>}
              {threads.map((thread) => (
                <div key={thread.id} className={`chat-thread ${currentThreadId === thread.id ? 'active' : ''}`}>
                  {editingThreadId === thread.id ? (
                    <div className="chat-thread-edit">
                      <input value={editingTitle} onChange={(e) => setEditingTitle(e.target.value)} />
                      <button type="button" className="icon-btn" onClick={() => renameThread(thread.id)} aria-label="Save chat name">
                        <Check size={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button type="button" className="chat-thread-title" onClick={() => loadThreadMessages(thread.id)}>
                        {thread.title}
                      </button>
                      <div className="chat-thread-actions">
                        <button type="button" className="icon-btn" onClick={() => { setEditingThreadId(thread.id); setEditingTitle(thread.title); }} aria-label="Rename chat">
                          <Edit3 size={13} />
                        </button>
                        <button type="button" className="icon-btn" onClick={() => removeThread(thread.id)} aria-label="Delete chat">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}

      <div className="query-layout query-layout--single">

        <div className="card chat-card">
          <div className="chat-stream schema-scroll" ref={chatStreamRef}>
            {messages.length === 0 && (
              <div className="empty-state chat-empty-state">
                <p>Ask about programme fit, comparisons, eligibility, locations, or participant evidence.</p>
                <div className="conversation-starter-grid">
                  {conversationStarters.map((starter, index) => (
                    <button
                      key={`${starter.label}-${index}`}
                      type="button"
                      className="conversation-starter-btn"
                      onClick={() => handleQuery(starter.message)}
                      disabled={loading}
                    >
                      {starter.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, index) => {
              if (msg.type === 'user') {
                return (
                  <div key={`user-${index}`} className="chat-message-row user">
                    <div className="message-speaker">You</div>
                    <div className="bubble user">
                      {Array.isArray(msg.files) && msg.files.length > 0 && (
                        <div className="message-files">
                          {msg.files.map((file, fileIndex) => (
                            <span key={`${file.filename}-${fileIndex}`} className="message-file-chip">
                              <Paperclip size={12} />
                              {file.filename}
                            </span>
                          ))}
                        </div>
                      )}
                      <div>{msg.text}</div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={`ai-${index}`} className="chat-message-row ai">
                  <div className="message-speaker">Agent</div>
                  <div className="bubble ai">
                    <div className="ai-message-text markdown-body">{renderMessageBody(msg.text)}</div>
                  </div>
                </div>
              );
            })}
            {loading && (
              <div className="chat-message-row ai">
                <div className="message-speaker">Agent</div>
                <div className="bubble ai typing-bubble" aria-label="Assistant is typing">
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </div>
              </div>
            )}
          </div>

          <div className="chat-composer-shell">
            {attachedFiles.length > 0 && (
              <div className="attached-file-row">
                {attachedFiles.map((file, index) => (
                  <span key={`${file.name}-${index}`} className="message-file-chip">
                    <Paperclip size={12} />
                    {file.name}
                    <button type="button" onClick={() => removeAttachedFile(index)} aria-label={`Remove ${file.name}`}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <textarea
              ref={textareaRef}
              className="chat-textarea"
              placeholder="Ask anything"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !loading) {
                  e.preventDefault();
                  handleQuery();
                }
              }}
              disabled={loading}
              rows={1}
            />
            <div className="chat-composer-toolbar">
              <div className="chat-composer-left">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden-file-input"
                  onChange={handleFileSelection}
                />
                <button type="button" className="composer-icon-btn add-file-btn" onClick={() => fileInputRef.current?.click()} aria-label="Attach files">
                  <Plus size={18} />
                </button>
                <button
                  type="button"
                  className={`composer-pill ${webSearchEnabled ? 'active' : ''}`}
                  onClick={() => setWebSearchEnabled((previous) => !previous)}
                >
                  <Globe2 size={15} />
                  Web
                </button>
                <div className="model-menu" ref={modelMenuRef}>
                  <button
                    type="button"
                    className="model-menu-trigger"
                    onClick={() => setModelMenuOpen((previous) => !previous)}
                    aria-haspopup="menu"
                    aria-expanded={modelMenuOpen}
                    title={modelLoadError || modelButtonLabel}
                  >
                    <span>{modelButtonLabel}</span>
                    <ChevronDown size={14} />
                  </button>
                  {modelMenuOpen && (
                    <div className="model-menu-popover" role="menu">
                      {models.length === 0 && (
                        <div className="model-menu-empty">
                          {modelLoadError || 'Loading models'}
                        </div>
                      )}
                      {models.map((item) => (
                        <div
                          key={item.id}
                          className="model-menu-row"
                          onMouseEnter={() => setHoveredReasoningModel(item.id)}
                          onFocus={() => setHoveredReasoningModel(item.id)}
                        >
                          <button type="button" onClick={() => selectModel(item)}>
                            <span>{item.label}</span>
                            {selectedModel === item.id && <Check size={13} />}
                          </button>
                          {item.supportsReasoning && (
                            <div className="reasoning-flyout-wrap">
                              <button
                                type="button"
                                className="reasoning-flyout-trigger"
                                aria-label={`Choose reasoning level for ${item.label}`}
                                onMouseEnter={() => setHoveredReasoningModel(item.id)}
                                onFocus={() => setHoveredReasoningModel(item.id)}
                              >
                                <ChevronRight size={14} />
                              </button>
                              {hoveredReasoningModel === item.id && (
                                <div className="reasoning-flyout">
                                  {item.reasoningEfforts.map((effort) => (
                                    <button
                                      key={`${item.id}-${effort}`}
                                      type="button"
                                      onClick={() => selectModel(item, effort)}
                                      className={selectedModel === item.id && reasoningEffort === effort ? 'active' : ''}
                                    >
                                      {effort}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button className="composer-send-btn" onClick={() => handleQuery()} disabled={loading || (!query.trim() && attachedFiles.length === 0)} aria-label="Send">
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QueryInterface;
